import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import Button from '../../../components/ui/Button';
import { membershipsService } from '../../../services/membershipsService';
import { postsService } from '../../../services/postsService';
import { advancedSearchService } from '../../../services/advancedSearchService';
import { creatorService } from '../../../services/creatorService';
import { useSelector } from 'react-redux';
import type { RootState } from '../../../store';
import PollDisplay from '../../../components/polls/PollDisplay';
import MembershipActions from '../../../components/membership/MembershipActions';
import ScheduledTierChangeModal from '../../../components/membership/ScheduledTierChangeModal';
import ScheduledTierChangesList from '../../../components/membership/ScheduledTierChangesList';
import TierComparisonTable from '../../../components/membership/TierComparisonTable';
import { useNotifications } from '../../../hooks/useNotifications';
// Shared branding constant
import { BRAND_NAME } from '../../../../../shared/constants/branding';
import {
  UserGroupIcon,
  HeartIcon,
  CurrencyDollarIcon,
  BellIcon,
  PlayIcon,
  EyeIcon,
  ClockIcon,
  PhotoIcon,
  VideoCameraIcon,
  MusicalNoteIcon,
  DocumentTextIcon,
  ChevronDownIcon,
  ArrowTrendingUpIcon,
  SparklesIcon,
  ChevronUpIcon,
} from '@heroicons/react/24/outline';
import { HeartIcon as HeartSolidIcon } from '@heroicons/react/24/solid';

interface Membership {
  id: string;
  creatorProfileId: string;
  creatorName: string;
  creatorAvatar?: string;
  creatorCustomUrl?: string;
  tierName: string;
  currentTierId?: string;
  monthlyAmount: number;
  since: string;
  status: 'active' | 'paused' | 'cancelled';
  benefits: string[];
  isInTrial?: boolean;
  trialEndsAt?: string;
  nextChargeDate?: string;
  _count?: {
    scheduledTierChanges: number;
  };
}

interface RecentPost {
  id: string;
  creatorName: string;
  creatorAvatar?: string;
  title: string;
  type: 'text' | 'image' | 'video' | 'audio' | 'poll';
  preview: string;
  timestamp: string;
  read: boolean;
  liked: boolean;
  likeCount: number;
  contentType?: string; // Full contentType for API posts
  mediaUrls?: string[]; // For poll options or media
}

interface ActivityItem {
  id: string;
  type: 'membership' | 'post' | 'like' | 'comment';
  message: string;
  timestamp: string;
  amount?: number;
}

const PatronDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.auth);
  const [memberships, setMemberships] = useState<Membership[]>([]);
  const [recentPosts, setRecentPosts] = useState<RecentPost[]>([]);
  const [activity, setActivity] = useState<ActivityItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'feed' | 'memberships' | 'activity'>('feed');
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [selectedMembershipId, setSelectedMembershipId] = useState<string | null>(null);
  const [feedPage, setFeedPage] = useState(1);
  const [feedHasMore, setFeedHasMore] = useState(true);
  const [feedLoading, setFeedLoading] = useState(false);
  const [expandedMembershipId, setExpandedMembershipId] = useState<string | null>(null);
  const [membershipTiers, setMembershipTiers] = useState<Record<string, any[]>>({});
  const [loadingTiers, setLoadingTiers] = useState<Record<string, boolean>>({});

  // Fetch unread notification count with auto-refresh every 30 seconds
  const { unreadCount: unreadNotifications } = useNotifications(30000);

  // Calculate days remaining in trial
  const getTrialDaysRemaining = (trialEndsAt: string): number => {
    const endDate = new Date(trialEndsAt);
    const now = new Date();
    const diffTime = endDate.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  };

  useEffect(() => {
    const loadDashboardData = async () => {
      setLoading(true);
      try {
        // Load memberships
        const res = await membershipsService.listMine({ limit: 20, offset: 0 });
        const normalizedMemberships = res.memberships.map((m: any) => ({
          id: m.id,
          creatorProfileId: m.creatorProfileId,
          creatorName: m.creatorProfile?.displayName || m.creatorProfile?.user?.firstName || m.creatorProfile?.user?.username || 'Creator',
          creatorAvatar: m.creatorProfile?.user?.avatar,
          creatorCustomUrl: m.creatorProfile?.customUrl,
          tierName: m.tier?.name,
          currentTierId: m.tierId,
          monthlyAmount: m.tier?.billingCycle === 'YEARLY' ? m.tier?.amount / 12 : m.tier?.amount,
          since: new Date(m.pledgeDate).toLocaleDateString('en-US', { year: 'numeric', month: 'short' }),
          status: m.status.toLowerCase(),
          benefits: m.tier?.benefits || [],
          isInTrial: m.isInTrial,
          trialEndsAt: m.trialEndsAt,
          nextChargeDate: m.nextChargeDate,
          _count: m._count
        }));
        setMemberships(normalizedMemberships);

        // Load posts from creators the user supports
        const activeMemberships = res.memberships.filter((m: any) => m.status === 'ACTIVE');
        if (activeMemberships.length > 0) {
          const creatorIds = activeMemberships.map((m: any) => m.creatorProfileId);
          
          // Use advanced search to get posts from supported creators
          try {
            const searchRes = await advancedSearchService.searchPosts(
              '*', // Match all posts
              {
                creatorIds,
                sortBy: 'newest',
              },
              1,
              20
            );
            
            const posts = searchRes.hits || [];
            setRecentPosts(posts.slice(0, 10).map((post: any) => ({
              id: post.id,
              creatorName: post.creator?.displayName || post.creator?.username || 'Creator',
              creatorAvatar: post.creator?.avatar,
              title: post.title || 'Untitled Post',
              type: post.contentType?.toLowerCase() as any,
              preview: post.content?.substring(0, 150) || '',
              timestamp: post.publishedAt ? new Date(post.publishedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'Recently',
              read: false,
              liked: false,
              likeCount: post.likeCount || 0,
              contentType: post.contentType,
              mediaUrls: post.mediaUrls
            })));
          } catch (searchError) {
            console.error('Failed to search posts:', searchError);
            // Fallback to regular posts list
            const postsRes = await postsService.list({
              limit: 20,
              offset: 0
            });
            
            const userPosts = (postsRes.posts || []).filter((post: any) => 
              creatorIds.includes(post.creatorId)
            );
            
            setRecentPosts(userPosts.slice(0, 10).map((post: any) => ({
              id: post.id,
              creatorName: post.creator?.creatorProfile?.displayName || post.creator?.username || 'Creator',
              creatorAvatar: post.creator?.avatar,
              title: post.title || 'Untitled Post',
              type: post.contentType.toLowerCase() as any,
              preview: post.content?.substring(0, 150) || '',
              timestamp: post.publishedAt ? new Date(post.publishedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'Recently',
              read: false,
              liked: false,
              likeCount: post._count?.likes || 0
            })));
          }
        } else {
          setRecentPosts([]);
        }

        setActivity([
          {
            id: '1',
            type: 'membership',
            message: 'Your monthly payment to Sarah Chen has been processed',
            timestamp: '1 day ago',
            amount: 29.99,
          },
          {
            id: '2',
            type: 'post',
            message: 'You liked "Behind the Scenes: New Art Process"',
            timestamp: '2 hours ago',
          },
          {
            id: '3',
            type: 'membership',
            message: 'You joined Gold tier for Sarah Chen',
            timestamp: '2 weeks ago',
            amount: 29.99,
          },
          {
            id: '4',
            type: 'comment',
            message: 'You commented on "Monthly Q&A Session Recording"',
            timestamp: '3 days ago',
          },
        ]);

        setLoading(false);
      } catch (error) {
        console.error('Failed to load dashboard data:', error);
        setLoading(false);
      }
    };

    loadDashboardData();
  }, []);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const getPostIcon = (type: string) => {
    switch (type) {
      case 'video':
        return <VideoCameraIcon className="w-4 h-4" />;
      case 'audio':
        return <MusicalNoteIcon className="w-4 h-4" />;
      case 'image':
        return <PhotoIcon className="w-4 h-4" />;
      case 'poll':
        return <DocumentTextIcon className="w-4 h-4" />;
      default:
        return <DocumentTextIcon className="w-4 h-4" />;
    }
  };

  // Toggle tier comparison view for a membership
  const toggleTierView = async (membershipId: string, creatorProfileId: string) => {
    if (expandedMembershipId === membershipId) {
      // Collapse if already expanded
      setExpandedMembershipId(null);
      return;
    }

    // Expand and fetch tiers if not already cached
    setExpandedMembershipId(membershipId);
    
    if (!membershipTiers[creatorProfileId] && !loadingTiers[creatorProfileId]) {
      setLoadingTiers(prev => ({ ...prev, [creatorProfileId]: true }));
      try {
        const tiers = await creatorService.getCreatorTiers(creatorProfileId);
        setMembershipTiers(prev => ({ ...prev, [creatorProfileId]: tiers }));
      } catch (error) {
        console.error('Failed to load tiers:', error);
      } finally {
        setLoadingTiers(prev => ({ ...prev, [creatorProfileId]: false }));
      }
    }
  };

  const calculateMonthlySpending = () => {
    return memberships
      .filter(m => m.status === 'active')
      .reduce((total, m) => total + m.monthlyAmount, 0);
  };

  const loadMorePosts = async () => {
    if (feedLoading || !feedHasMore) return;

    setFeedLoading(true);
    try {
      const activeMemberships = memberships.filter(m => m.status === 'active');
      if (activeMemberships.length === 0) {
        setFeedHasMore(false);
        return;
      }

  const creatorIds = activeMemberships.map(m => m.creatorProfileId);
      const nextPage = feedPage + 1;

      const searchRes = await advancedSearchService.searchPosts(
        '*',
        { creatorIds, sortBy: 'newest' },
        nextPage,
        10
      );

      const newPosts = (searchRes.hits || []).map((post: any) => ({
        id: post.id,
        creatorName: post.creator?.displayName || post.creator?.username || 'Creator',
        creatorAvatar: post.creator?.avatar,
        title: post.title || 'Untitled Post',
        type: post.contentType?.toLowerCase() as any,
        preview: post.content?.substring(0, 150) || '',
        timestamp: post.publishedAt ? new Date(post.publishedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'Recently',
        read: false,
        liked: false,
        likeCount: post.likeCount || 0,
        contentType: post.contentType,
        mediaUrls: post.mediaUrls
      }));

      setRecentPosts(prev => [...prev, ...newPosts]);
      setFeedPage(nextPage);
      setFeedHasMore(newPosts.length >= 10);
    } catch (error) {
      console.error('Failed to load more posts:', error);
      setFeedHasMore(false);
    } finally {
      setFeedLoading(false);
    }
  };

  const totalMonthlySpend = useMemo(calculateMonthlySpending, [memberships]);

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-6"></div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-32 bg-gray-200 rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6" data-brand={BRAND_NAME}>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">
          Welcome back, {user?.firstName || 'Patron'}! 👋
        </h1>
        <p className="text-gray-600 mt-2">
          Manage your memberships and discover exclusive content from your favorite creators.
        </p>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0 }}
          className="relative overflow-hidden rounded-xl shadow-lg"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-purple-500 to-pink-600 opacity-90"></div>
          <div className="relative backdrop-blur-sm bg-white/20 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-white/30 rounded-lg backdrop-blur-sm">
                <UserGroupIcon className="w-6 h-6 text-white" />
              </div>
              <ArrowTrendingUpIcon className="w-5 h-5 text-white/80" />
            </div>
            <h3 className="text-3xl font-bold text-white">{memberships.filter(m => m.status === 'active').length}</h3>
            <p className="text-white/90 text-sm mt-1 font-medium">Active Memberships</p>
          </div>
        </motion.div>

        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="relative overflow-hidden rounded-xl shadow-lg"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-emerald-500 to-teal-600 opacity-90"></div>
          <div className="relative backdrop-blur-sm bg-white/20 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-white/30 rounded-lg backdrop-blur-sm">
                <CurrencyDollarIcon className="w-6 h-6 text-white" />
              </div>
              <SparklesIcon className="w-5 h-5 text-white/80" />
            </div>
            <h3 className="text-3xl font-bold text-white">{formatCurrency(totalMonthlySpend)}</h3>
            <p className="text-white/90 text-sm mt-1 font-medium">Monthly Spending</p>
          </div>
        </motion.div>

        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="relative overflow-hidden rounded-xl shadow-lg"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-indigo-600 opacity-90"></div>
          <div className="relative backdrop-blur-sm bg-white/20 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-white/30 rounded-lg backdrop-blur-sm">
                <BellIcon className="w-6 h-6 text-white" />
              </div>
              {unreadNotifications > 0 && (
                <span className="bg-red-500 text-white px-2 py-1 rounded-full text-xs font-bold min-w-[24px] text-center shadow-lg">
                  {unreadNotifications > 99 ? '99+' : unreadNotifications}
                </span>
              )}
            </div>
            <h3 className="text-3xl font-bold text-white">{unreadNotifications}</h3>
            <p className="text-white/90 text-sm mt-1 font-medium">Unread Notifications</p>
          </div>
        </motion.div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-8">
        <nav className="-mb-px flex space-x-8" role="tablist" aria-label="Patron dashboard tabs">
          <button
            onClick={() => setActiveTab('feed')}
            role="tab"
            aria-selected={activeTab === 'feed'}
            aria-controls="tab-panel-feed"
            className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'feed'
                  ? 'text-[color:var(--patreon-orange)]'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Feed
          </button>
          <button
            onClick={() => setActiveTab('memberships')}
            role="tab"
            aria-selected={activeTab === 'memberships'}
            aria-controls="tab-panel-memberships"
            className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'memberships'
                  ? 'text-[color:var(--patreon-orange)]'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Memberships ({memberships.filter(m => m.status === 'active').length})
          </button>
          <button
            onClick={() => setActiveTab('activity')}
            role="tab"
            aria-selected={activeTab === 'activity'}
            aria-controls="tab-panel-activity"
            className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'activity'
                  ? 'text-[color:var(--patreon-orange)]'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Activity
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'feed' && (
        <div id="tab-panel-feed" role="tabpanel" aria-labelledby="tab-feed" className="space-y-6">
          {recentPosts.length === 0 ? (
            <div className="text-center py-12">
              <DocumentTextIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No posts yet</h3>
              <p className="text-gray-600">Start supporting creators to see their exclusive content here.</p>
            </div>
          ) : (
            recentPosts.map((post, index) => (
              <motion.div 
                key={post.id} 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-lg hover:border-purple-200 transition-all duration-300"
              >
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    {post.creatorAvatar ? (
                      <img
                        src={post.creatorAvatar}
                        alt={post.creatorName}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-12 h-12 bg-gradient-to-r from-orange-400 to-pink-500 rounded-full flex items-center justify-center text-white font-semibold">
                        {post.creatorName.charAt(0)}
                      </div>
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-semibold text-gray-900">{post.creatorName}</h3>
                        <p className="text-sm text-gray-500">{post.timestamp}</p>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          post.type === 'video' ? 'bg-red-100 text-red-700' :
                          post.type === 'audio' ? 'bg-purple-100 text-purple-700' :
                          post.type === 'image' ? 'bg-green-100 text-green-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {getPostIcon(post.type)}
                          <span className="ml-1 capitalize">{post.type}</span>
                        </span>
                        {!post.read && (
                          <span className="bg-orange-100 text-orange-700 px-2 py-1 rounded-full text-xs font-medium">
                            New
                          </span>
                        )}
                      </div>
                    </div>

                    <h4 className="font-medium text-gray-900 mt-2">{post.title}</h4>
                    {post.type !== 'poll' && (
                      <p className="text-gray-600 mt-2 line-clamp-2">{post.preview}</p>
                    )}

                    {/* Poll Display */}
                    {post.type === 'poll' && post.contentType === 'POLL' && post.mediaUrls && post.mediaUrls.length > 0 && (
                      <div className="mt-4">
                        <PollDisplay
                          postId={post.id}
                          pollOptions={post.mediaUrls}
                          onVote={() => {
                            // Vote submitted
                          }}
                        />
                      </div>
                    )}

                    <div className="flex items-center justify-between mt-4">
                      <button
                        className="flex items-center space-x-1 text-gray-600 hover:text-red-600 transition-colors"
                        onClick={async () => {
                          // Optimistic update
                          setRecentPosts(prev => prev.map(p =>
                            p.id === post.id
                              ? { ...p, liked: !p.liked, likeCount: p.liked ? Math.max(0, p.likeCount - 1) : p.likeCount + 1 }
                              : p
                          ));
                          try {
                            const res = await postsService.like(post.id);
                            setRecentPosts(prev => prev.map(p =>
                              p.id === post.id
                                ? { ...p, liked: res.isLiked }
                                : p
                            ));
                          } catch (e) {
                            // Revert on error
                            setRecentPosts(prev => prev.map(p =>
                              p.id === post.id
                                ? { ...p, liked: post.liked, likeCount: post.likeCount }
                                : p
                            ));
                          }
                        }}
                        aria-label={post.liked ? 'Unlike post' : 'Like post'}
                      >
                        {post.liked ? (
                          <HeartSolidIcon className="w-5 h-5 text-red-600" />
                        ) : (
                          <HeartIcon className="w-5 h-5" />
                        )}
                        <span className="text-sm">{post.likeCount}</span>
                      </button>
                      <button className="flex items-center space-x-1 text-gray-600 hover:text-blue-600 transition-colors" aria-label="View full post">
                        <EyeIcon className="w-5 h-5" />
                        <span className="text-sm">View full post</span>
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))
          )}
          
          {/* Load More Button */}
          {recentPosts.length > 0 && feedHasMore && (
            <div className="text-center py-6">
              <button
                onClick={loadMorePosts}
                disabled={feedLoading}
                className="bg-gradient-to-r from-orange-400 to-pink-500 text-white px-8 py-3 rounded-lg font-medium hover:shadow-lg transition-shadow disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {feedLoading ? (
                  <>
                    <span className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></span>
                    Loading...
                  </>
                ) : (
                  'Load More Posts'
                )}
              </button>
            </div>
          )}
        </div>
      )}

      {activeTab === 'memberships' && (
        <div id="tab-panel-memberships" role="tabpanel" aria-labelledby="tab-memberships" className="space-y-6">
          {memberships.length === 0 ? (
            <div className="text-center py-12">
              <UserGroupIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No memberships yet</h3>
              <p className="text-gray-600 mb-4">Start supporting your favorite creators to see their exclusive content.</p>
              <button
                className="text-white px-6 py-2 rounded-lg font-medium hover:shadow-lg transition-shadow patreon-primary-gradient"
                style={{ background: 'var(--patreon-btn-primary-bg)', boxShadow: 'var(--patreon-btn-primary-shadow)' }}
                onClick={() => navigate('/discover')}
              >
                Discover Creators
              </button>
            </div>
          ) : (
            memberships.map((membership, index) => (
              <motion.div 
                key={membership.id} 
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-lg hover:border-emerald-200 transition-all duration-300"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    {membership.creatorAvatar ? (
                      <img
                        src={membership.creatorAvatar}
                        alt={membership.creatorName}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-12 h-12 bg-gradient-to-r from-orange-400 to-pink-500 rounded-full flex items-center justify-center text-white font-semibold">
                        {membership.creatorName.charAt(0)}
                      </div>
                    )}
                    <div>
                      <h3 className="font-semibold text-gray-900">{membership.creatorName}</h3>
                      <p className="text-sm text-gray-500">Member since {membership.since}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-gray-900">{formatCurrency(membership.monthlyAmount)}/month</p>
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      membership.status === 'active' ? 'bg-green-100 text-green-700' :
                      membership.status === 'paused' ? 'bg-yellow-100 text-yellow-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {membership.status}
                    </span>
                  </div>
                </div>

                <div className="mt-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-gray-900">{membership.tierName}</p>
                      {membership._count?.scheduledTierChanges && membership._count.scheduledTierChanges > 0 && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                          {membership._count.scheduledTierChanges} pending change{membership._count.scheduledTierChanges > 1 ? 's' : ''}
                        </span>
                      )}
                    </div>
                    {membership.isInTrial && membership.trialEndsAt && (() => {
                      const daysRemaining = getTrialDaysRemaining(membership.trialEndsAt);
                      const isExpiringSoon = daysRemaining <= 3;
                      return (
                        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${
                          isExpiringSoon ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700'
                        }`}>
                          🎁 
                          {daysRemaining === 0 ? (
                            <span>Trial ends today</span>
                          ) : daysRemaining === 1 ? (
                            <span>1 day left in trial</span>
                          ) : (
                            <span>{daysRemaining} days left in trial</span>
                          )}
                        </span>
                      );
                    })()}
                  </div>
                  <ul className="space-y-1">
                    {membership.benefits.map((benefit, index) => (
                      <li key={index} className="flex items-center text-sm text-gray-600">
                        <svg className="w-4 h-4 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        {benefit}
                      </li>
                    ))}
                  </ul>
                  {!membership.isInTrial && membership.nextChargeDate && membership.status === 'active' && (
                    <p className="text-sm text-gray-500 mt-2">
                      Next charge: {new Date(membership.nextChargeDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </p>
                  )}
                </div>

                <div className="mt-6">
                  <MembershipActions
                    membership={{
                      id: membership.id,
                      status: membership.status.toUpperCase() as 'ACTIVE' | 'PAUSED' | 'CANCELLED' | 'EXPIRED',
                      tier: {
                        id: '',
                        name: membership.tierName,
                      },
                    }}
                    onPause={async (id) => {
                      await membershipsService.pause(id);
                      const res = await membershipsService.listMine({ limit: 20, offset: 0 });
                      setMemberships(res.memberships.map((m: any) => ({
                        creatorProfileId: m.creatorProfileId,
                        id: m.id,
                        creatorName: m.creatorProfile?.displayName || m.creatorProfile?.user?.username || 'Creator',
                        currentTierId: m.tierId,
                        creatorAvatar: m.creatorProfile?.user?.avatar,
                        creatorCustomUrl: m.creatorProfile?.customUrl,
                        tierName: m.tier?.name,
                        monthlyAmount: m.tier?.billingCycle === 'YEARLY' ? m.tier?.amount / 12 : m.tier?.amount,
                        since: new Date(m.pledgeDate).toLocaleDateString('en-US', { year: 'numeric', month: 'short' }),
                        status: m.status.toLowerCase(),
                        benefits: m.tier?.benefits || [],
                        isInTrial: m.isInTrial,
                        trialEndsAt: m.trialEndsAt,
                        nextChargeDate: m.nextChargeDate
                      })));
                    }}
                    onResume={async (id) => {
                      await membershipsService.resume(id);
                      const res = await membershipsService.listMine({ limit: 20, offset: 0 });
                      setMemberships(res.memberships.map((m: any) => ({
                        id: m.id,
                        creatorProfileId: m.creatorProfileId,
                        creatorName: m.creatorProfile?.displayName || m.creatorProfile?.user?.username || 'Creator',
                        creatorAvatar: m.creatorProfile?.user?.avatar,
                        creatorCustomUrl: m.creatorProfile?.customUrl,
                        tierName: m.tier?.name,
                        currentTierId: m.tierId,
                        monthlyAmount: m.tier?.billingCycle === 'YEARLY' ? m.tier?.amount / 12 : m.tier?.amount,
                        since: new Date(m.pledgeDate).toLocaleDateString('en-US', { year: 'numeric', month: 'short' }),
                        status: m.status.toLowerCase(),
                        benefits: m.tier?.benefits || [],
                        isInTrial: m.isInTrial,
                        trialEndsAt: m.trialEndsAt,
                        nextChargeDate: m.nextChargeDate,
                        _count: m._count
                      })));
                    }}
                    onCancel={async (id) => {
                      await membershipsService.cancel(id, true);
                      const res = await membershipsService.listMine({ limit: 20, offset: 0 });
                      setMemberships(res.memberships.map((m: any) => ({
                        id: m.id,
                        creatorProfileId: m.creatorProfileId,
                        creatorName: m.creatorProfile?.displayName || m.creatorProfile?.user?.username || 'Creator',
                        creatorAvatar: m.creatorProfile?.user?.avatar,
                        creatorCustomUrl: m.creatorProfile?.customUrl,
                        tierName: m.tier?.name,
                        currentTierId: m.tierId,
                        monthlyAmount: m.tier?.billingCycle === 'YEARLY' ? m.tier?.amount / 12 : m.tier?.amount,
                        since: new Date(m.pledgeDate).toLocaleDateString('en-US', { year: 'numeric', month: 'short' }),
                        status: m.status.toLowerCase(),
                        benefits: m.tier?.benefits || [],
                        isInTrial: m.isInTrial,
                        trialEndsAt: m.trialEndsAt,
                        nextChargeDate: m.nextChargeDate,
                        _count: m._count
                      })));
                    }}
                    onScheduleTierChange={async (id) => {
                      // Prefetch tiers for this membership's creator if not cached
                      const m = memberships.find(mem => mem.id === id);
                      if (m && !membershipTiers[m.creatorProfileId] && !loadingTiers[m.creatorProfileId]) {
                        setLoadingTiers(prev => ({ ...prev, [m.creatorProfileId]: true }));
                        try {
                          const tiers = await creatorService.getCreatorTiers(m.creatorProfileId);
                          setMembershipTiers(prev => ({ ...prev, [m.creatorProfileId]: tiers }));
                        } catch (e) {
                          console.error('Failed to prefetch tiers for scheduled change:', e);
                        } finally {
                          setLoadingTiers(prev => ({ ...prev, [m.creatorProfileId]: false }));
                        }
                      }
                      setSelectedMembershipId(id);
                      setShowScheduleModal(true);
                    }}
                    isLoading={false}
                  />
                  <Button 
                    className="flex-1 mt-3"
                    onClick={() => {
                      const url = membership.creatorCustomUrl || membership.creatorName;
                      navigate(`/creator/${url}`);
                    }}
                  >
                    View Page
                  </Button>
                {/* Tier Comparison */}
                <div className="mt-4 border-t pt-4">
                  <button
                    onClick={() => toggleTierView(membership.id, membership.creatorProfileId)}
                    aria-expanded={expandedMembershipId === membership.id}
                    aria-controls={`tiers-panel-${membership.id}`}
                    className="flex items-center justify-between w-full text-left px-4 py-2 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors"
                  >
                    <span className="font-medium text-gray-900">
                      {expandedMembershipId === membership.id ? 'Hide' : 'View'} Available Tiers
                    </span>
                    {expandedMembershipId === membership.id ? (
                      <ChevronUpIcon className="w-5 h-5 text-gray-600" />
                    ) : (
                      <ChevronDownIcon className="w-5 h-5 text-gray-600" />
                    )}
                  </button>

                  {expandedMembershipId === membership.id && (
                    <div id={`tiers-panel-${membership.id}`} role="region" aria-label="Available tiers" className="mt-4">
                      {loadingTiers[membership.creatorProfileId] ? (
                        <div className="text-center py-8">
                          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
                          <p className="text-gray-600 mt-2">Loading tiers...</p>
                        </div>
                      ) : membershipTiers[membership.creatorProfileId] ? (
                        <TierComparisonTable
                          tiers={membershipTiers[membership.creatorProfileId]}
                          currentTierId={membership.currentTierId}
                          onSelectTier={(tierId, action) => {
                            if (action !== 'current') {
                              setSelectedMembershipId(membership.id);
                              setShowScheduleModal(true);
                            }
                          }}
                        />
                      ) : (
                        <div className="text-center py-8 text-gray-600">
                          <p>No tiers available</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                </div>

                {/* Scheduled Tier Changes */}
                <div className="mt-4">
                  <ScheduledTierChangesList membershipId={membership.id} />
                </div>
              </motion.div>
            ))
          )}
        </div>
      )}

      {activeTab === 'activity' && (
        <div id="tab-panel-activity" role="tabpanel" aria-labelledby="tab-activity" className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="divide-y divide-gray-200">
            {activity.length === 0 ? (
              <div className="p-12 text-center">
                <ClockIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No activity yet</h3>
                <p className="text-gray-600">Your activity will appear here as you engage with creators.</p>
              </div>
            ) : (
              activity.map((item, index) => (
                <motion.div 
                  key={item.id} 
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                  className="p-6 hover:bg-gradient-to-r hover:from-purple-50 hover:to-pink-50 transition-all duration-300"
                >
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 mt-1">
                      {item.type === 'membership' && <CurrencyDollarIcon className="w-5 h-5 text-green-600" />}
                      {item.type === 'post' && <DocumentTextIcon className="w-5 h-5 text-blue-600" />}
                      {item.type === 'like' && <HeartIcon className="w-5 h-5 text-red-600" />}
                      {item.type === 'comment' && <PlayIcon className="w-5 h-5 text-purple-600" />}
                    </div>
                    <div className="flex-1">
                      <p className="text-gray-900">{item.message}</p>
                      <div className="flex items-center mt-2 text-sm text-gray-500">
                        <ClockIcon className="w-4 h-4 mr-1" />
                        {item.timestamp}
                        {item.amount && (
                          <span className="ml-3 font-medium text-green-600">
                            {formatCurrency(item.amount)}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Scheduled Tier Change Modal */}
      {showScheduleModal && selectedMembershipId && (() => {
        const membership = memberships.find(m => m.id === selectedMembershipId);
        if (!membership) return null;

        const tiersForCreator = membershipTiers[membership.creatorProfileId] || [];
        // Try to locate the current tier by id from fetched tiers
        const fetchedCurrent = tiersForCreator.find((t: any) => t.id === membership.currentTierId);
        const currentTier = fetchedCurrent || {
          id: membership.currentTierId || 'current-tier',
          name: membership.tierName,
          amount: membership.monthlyAmount,
          currency: 'USD',
          description: '',
          benefits: membership.benefits
        };

        const availableTiers = tiersForCreator.length > 0 ? tiersForCreator : [currentTier];

        return (
          <ScheduledTierChangeModal
            membershipId={selectedMembershipId}
            isOpen={showScheduleModal}
            onClose={() => {
              setShowScheduleModal(false);
              setSelectedMembershipId(null);
            }}
            currentTier={currentTier}
            availableTiers={availableTiers}
            onSuccess={() => {
              setShowScheduleModal(false);
              setSelectedMembershipId(null);
              // Optionally refresh memberships
            }}
          />
        );
      })()}
    </div>
  );
};

export default PatronDashboard;
